import { ref } from 'vue';

export function useConfetti() {
  let confettiAnimationId = null;
  const confettiPieces = [];
  let animationStartTime = null;
  const ANIMATION_DURATION = 1500;

  class ConfettiPiece {
    constructor(canvas) {
      this.canvas = canvas;
      this.reset();
      this.y = Math.random() * canvas.height; // Start at random positions for initial load
      this.opacity = 1; // Add opacity property
    }

    reset() {
      this.x = Math.random() * this.canvas.width;
      this.y = -10;
      this.vx = (Math.random() - 0.5) * 2;
      this.vy = Math.random() * 3 + 2;
      this.rotation = Math.random() * 360;
      this.rotationSpeed = (Math.random() - 0.5) * 10;
      this.size = Math.random() * 8 + 4;
      this.colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
      this.color = this.colors[Math.floor(Math.random() * this.colors.length)];
      this.shape = Math.random() > 0.5 ? 'square' : 'circle';
      this.opacity = 1; // Reset opacity
    }

    update(elapsedTime) {
      this.x += this.vx;
      this.y += this.vy;
      this.rotation += this.rotationSpeed;
      this.vy += 0.1; // gravity

      // Fade out after 3 seconds
      if (elapsedTime > ANIMATION_DURATION) {
        const fadeTime = elapsedTime - ANIMATION_DURATION;
        const fadeDuration = 1000; // 1 second fade
        this.opacity = Math.max(0, 1 - (fadeTime / fadeDuration));
      }

      // Reset when off screen or fully faded
      if (this.y > this.canvas.height + 10 || 
          this.x < -10 || 
          this.x > this.canvas.width + 10 || 
          this.opacity <= 0) {
        this.reset();
      }
    }

    draw(ctx) {
      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.rotate((this.rotation * Math.PI) / 180);
      ctx.globalAlpha = this.opacity;
      ctx.fillStyle = this.color;

      if (this.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
      }
      ctx.restore();
    }
  }

  function initConfetti(canvas) {
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Create confetti pieces
    confettiPieces.length = 0;
    for (let i = 0; i < 100; i += 1) {
      confettiPieces.push(new ConfettiPiece(canvas));
    }

    // Animation loop
    function animate(currentTime) {
      if (!animationStartTime) {
        animationStartTime = currentTime;
      }

      const elapsedTime = currentTime - animationStartTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      confettiPieces.forEach((piece) => {
        piece.update(elapsedTime);
        piece.draw(ctx);
      });

      // Stop animation after fade out is complete
      if (elapsedTime > ANIMATION_DURATION + 1000) {
        stopConfetti();
        return;
      }

      confettiAnimationId = requestAnimationFrame(animate);
    }

    animate();
  }

  function stopConfetti() {
    if (confettiAnimationId) {
      cancelAnimationFrame(confettiAnimationId);
      confettiAnimationId = null;
      animationStartTime = null;
    }
  }

  function triggerConfettiBurst(canvas) {
    // Create additional confetti pieces for burst effect
    if (!canvas) return;

    for (let i = 0; i < 50; i += 1) {
      const piece = new ConfettiPiece(canvas);
      piece.x = canvas.width / 2 + (Math.random() - 0.5) * 200;
      piece.y = canvas.height / 2;
      piece.vy = -(Math.random() * 10 + 5);
      piece.vx = (Math.random() - 0.5) * 10;
      confettiPieces.push(piece);
    }

    // Remove extra pieces after some time
    setTimeout(() => {
      confettiPieces.splice(100, 50);
    }, 2000);
  }

  return {
    initConfetti,
    stopConfetti,
    triggerConfettiBurst,
  };
} 